// Plan / feature-gating utilities (Light vs Pro)
//
// The pharmacy's plan is expected to arrive from the backend at login time
// (most likely as a claim inside the JWT, e.g. `plan: "light" | "pro"`), and
// to be stored on the `user` object as `user.plan`.
//
// While that backend integration is still being decided, the plan can also be
// forced locally for testing via a dev override (see AuthProvider + the header
// toggle). Resolution order is: dev override > user.plan > DEFAULT_PLAN.

export const PLANS = {
  LIGHT: 'light',
  PRO: 'pro',
}

// Feature keys used across the app for gating.
export const FEATURES = {
  DASHBOARD: 'dashboard',
  PRODUCTS: 'products',
  STOCKOUTS: 'stockouts',
  INVOICES: 'invoices',
  FORECASTING: 'forecasting',
  DEMAND_ANALYTICS: 'demand_analytics',
  ORDERS: 'orders',
  MOLECULES: 'molecules',
  SUPPLIERS: 'suppliers',
  CHATBOT: 'chatbot',
  REPORTS: 'reports',
  DIRECT_PURCHASE: 'direct_purchase',
  UNLIMITED_PORTFOLIO: 'unlimited_portfolio',
}

// Which features each plan unlocks.
// LIGHT is intentionally useful on its own (daily habit) while keeping the
// value-generating features (forecasting, analytics, AI, reports) behind Pro.
export const PLAN_FEATURES = {
  [PLANS.LIGHT]: [FEATURES.DASHBOARD, FEATURES.PRODUCTS, FEATURES.STOCKOUTS, FEATURES.INVOICES],
  [PLANS.PRO]: [
    FEATURES.DASHBOARD,
    FEATURES.PRODUCTS,
    FEATURES.STOCKOUTS,
    FEATURES.INVOICES,
    FEATURES.FORECASTING,
    FEATURES.DEMAND_ANALYTICS,
    FEATURES.ORDERS,
    FEATURES.MOLECULES,
    FEATURES.SUPPLIERS,
    FEATURES.CHATBOT,
    FEATURES.REPORTS,
    FEATURES.DIRECT_PURCHASE,
    FEATURES.UNLIMITED_PORTFOLIO,
  ],
}

// Usage caps for plans that do not unlock the matching "unlimited" feature.
// Keyed by the feature that lifts the cap, so the check stays fail-closed: any
// plan without FEATURES.CHATBOT / FEATURES.UNLIMITED_PORTFOLIO gets the cap.
export const LIGHT_LIMITS = {
  CHATBOT_MESSAGES_PER_MONTH: 5,
  ACTIVE_PRODUCTS: 400,
}

// User-facing labels for upgrade CTAs (PT).
export const FEATURE_LABELS = {
  [FEATURES.FORECASTING]: 'Previsão de vendas',
  [FEATURES.DEMAND_ANALYTICS]: 'Análise de procura',
  [FEATURES.ORDERS]: 'Encomendas sugeridas',
  [FEATURES.MOLECULES]: 'Análise por molécula',
  [FEATURES.SUPPLIERS]: 'Ranking de laboratórios',
  [FEATURES.CHATBOT]: 'Bestie sem limites (mensagens ilimitadas)',
  [FEATURES.REPORTS]: 'Relatórios e exportações',
  [FEATURES.DIRECT_PURCHASE]: 'Compra direta',
  [FEATURES.UNLIMITED_PORTFOLIO]: `Portfólio ilimitado (Light: até ${LIGHT_LIMITS.ACTIVE_PRODUCTS} produtos ativos)`,
}

// Default plan when the backend has not (yet) provided one. Defaults to PRO so
// that existing users keep full functionality until the backend starts sending
// the plan claim. New Light pharmacies will carry `plan: "light"` explicitly.
export const DEFAULT_PLAN = PLANS.PRO

// localStorage key for the dev-only plan override.
export const DEV_PLAN_OVERRIDE_KEY = 'dev_plan_override'

/**
 * Resolve the effective plan from a dev override and the user object.
 * @param {object|null} user - the authenticated user (may carry `plan`)
 * @param {string|null} devOverride - dev-forced plan, or null/undefined
 * @returns {string} one of PLANS
 */
export const resolvePlan = (user, devOverride) => {
  if (devOverride === PLANS.LIGHT || devOverride === PLANS.PRO) {
    return devOverride
  }
  // Backend sends the plan in the login response (e.g. { plan: "pro" }); it is
  // persisted as part of `user`. Normalise so casing/whitespace never matters.
  const plan = typeof user?.plan === 'string' ? user.plan.trim().toLowerCase() : null
  return plan === PLANS.LIGHT ? PLANS.LIGHT : plan === PLANS.PRO ? PLANS.PRO : DEFAULT_PLAN
}

/**
 * Whether a given plan unlocks a given feature.
 *
 * Fail-closed: an unknown/unrecognised plan resolves to no unlocked features
 * (deny) rather than falling back to DEFAULT_PLAN, and an unknown feature key is
 * treated as gated (deny) rather than unlocked. A tampered, malformed, or
 * unexpected plan/feature value therefore loses access instead of gaining Pro
 * features. Legitimate callers always pass a plan from resolvePlan (light/pro)
 * and a feature key from FEATURES, all of which remain explicitly handled.
 * @param {string} plan
 * @param {string} feature
 * @returns {boolean}
 */
export const planHasFeature = (plan, feature) => {
  if (!feature) return false
  const features = PLAN_FEATURES[plan]
  if (!features) return false
  return features.includes(feature)
}
